// Shared timing of the field validation, so every field behaves the same.
//
// Three rules, and the difference between them is what the trigger is for:
//
//   1. A format check (URL, email, a number in range) speaks when the field loses focus,
//      never while the user is still typing.
//   2. Typing or deleting in a field that is showing a message clears it right away. It is
//      not checked again until the next blur or the next submit, so nothing flickers.
//   3. The obligation of a required field is only raised when the form is sent, or when a
//      stepped form tries to move on. Leaving an empty field never accuses the user.
//
// A field registers itself with the AppForm above it, which validates all of them on submit.
// See "Errors, in two levels" in the README of the frontend.

import { computed, ComputedRef, getCurrentInstance, inject, InjectionKey, onBeforeUnmount, provide, ref, Ref } from "vue";

/** What made the check run. A validator can behave differently in each case. */
export interface FieldValidationContext {
    trigger: "blur" | "submit";
}

export type FieldValidator = (value: any, context?: FieldValidationContext) => string;

export interface FieldValidationRegistry {
    register: (validate: () => string) => void;
    unregister: (validate: () => string) => void;
    // Told when any field of the form is edited, so the summary next to the actions can go
    // too: it was talking about the value the user just changed
    notifyEdited: () => void;
}

export const FORM_VALIDATION_REGISTRY: InjectionKey<FieldValidationRegistry> = Symbol("form-validation-registry");

export interface FieldValidationProps {
    validator?: FieldValidator;
    error?: string;
    disabled?: boolean;
}

export interface FieldValidation {
    touched: Ref<boolean>;
    // The message this field computed on its own
    validationError: Ref<string>;
    // What the field actually shows: the message of the parent wins, since it carries
    // the result of the submit and the answer of the server
    shownError: ComputedRef<string>;
    onFieldBlur: () => void;
    onFieldInput: () => void;
    validateNow: () => string;
}

/**
 * Wires the validation of a single field. Call it from setup() of a field component,
 * passing the props of the component and a getter for its current value.
 */
export function useFieldValidation(props: FieldValidationProps, getValue: () => any): FieldValidation {
    const instance = getCurrentInstance();
    const touched = ref(false);
    const validationError = ref("");

    const publish = (message: string) => {
        validationError.value = message;

        // Kept in sync with the parent, so a field bound with v-model:error clears the
        // message it was given on submit as soon as the value is edited
        instance?.proxy?.$emit("update:error", message);
    };

    const run = (trigger: "blur" | "submit"): string => {
        // A disabled field cannot be fixed by the user, so holding the form back on it
        // would be a dead end
        if (props.disabled) {
            return "";
        }

        const message = props.validator ? props.validator(getValue(), { trigger }) || "" : "";

        publish(message);

        return message;
    };

    const registry = inject(FORM_VALIDATION_REGISTRY, null);

    const shownError = computed(() => props.error || validationError.value);

    const onFieldBlur = () => {
        touched.value = true;

        // Leaving a field without touching it must not wipe the message the server gave
        if (props.validator) {
            run("blur");
        }
    };

    const onFieldInput = () => {
        // Typing never validates: it only takes the message away, until the next blur or
        // the next submit says otherwise. A field with no validator drops the message of
        // the server the same way, because editing the value made that answer stale
        if (shownError.value) {
            publish("");
        }

        // and the summary of the form goes with it: it was talking about this value
        registry?.notifyEdited();
    };

    const validateNow = (): string => {
        if (!props.validator || props.disabled) {
            return "";
        }

        touched.value = true;

        return run("submit");
    };

    if (registry) {
        registry.register(validateNow);
        onBeforeUnmount(() => registry.unregister(validateNow));
    }

    return { touched, validationError, shownError, onFieldBlur, onFieldInput, validateNow };
}

/**
 * Creates the registry an AppForm hands down to its fields. Call it from setup() of AppForm.
 */
export function provideFieldValidationRegistry(onEdited: () => void): Set<() => string> {
    const validators = new Set<() => string>();

    provide(FORM_VALIDATION_REGISTRY, {
        register: (validate: () => string) => {
            validators.add(validate);
        },
        unregister: (validate: () => string) => {
            validators.delete(validate);
        },
        notifyEdited: onEdited,
    });

    return validators;
}
